import React, { useState } from "react";
import { IProject } from "../../../@types";
import MetricCard from "./MetricCard";
import ProfitabilityChart from "./ProfitabilityChart";
import ActionCard from "./ActionCard";
import { IoChevronBack, IoChevronDown } from "react-icons/io5";
import ExpenseIconOrange from "../../../assets/icons/expenseIconOrange";
import InvoiceIconOrange from "../../../assets/icons/invoiceIconOrange";
import OptionAction from "./OptionAction";
import DescriptionModal from "./DescriptionModal";
import { Link } from "react-router-dom";
import { useSubscription } from "../../../context/SubscriptionProvider";

interface ViewContainerProps {
  project: IProject;
  onBack?: () => void;
  onAddExistingExpense?: () => void;
  onAddExpense?: () => void;
  onAddExistingInvoice?: () => void;
  onAddInvoice?: () => void;
  isLoading: boolean;
}

export enum PriorityLevel {
  low = "low",
  medium = "medium",
  high = "high",
}

export enum ProjectStatus {
  not_started = "not-started",
  in_progress = "in-progress",
  completed = "completed",
  cancelled = "cancelled",
}

const ViewContainer: React.FC<ViewContainerProps> = ({
  project,
  onBack,
  onAddExistingExpense,
  onAddExpense,
  onAddExistingInvoice,
  onAddInvoice,
  isLoading,
}) => {
  const { canUseModule, triggerSubscriptionModal } = useSubscription();
  const [isShowAddExisting, setIsShowAddExisting] = useState(true);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // const getStatusDisplay = (status: ProjectStatus) => {
  //   switch (status) {
  //     case ProjectStatus.not_started:
  //       return "Not started";
  //     case ProjectStatus.in_progress:
  //       return "In progress";
  //     case ProjectStatus.completed:
  //       return "Completed";
  //     case ProjectStatus.cancelled:
  //       return "Cancelled";
  //     default:
  //       return status;
  //   }
  // };

  // Function to truncate text
  const truncateText = (
    text: string | undefined,
    maxLength: number,
  ): string => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  // Calculate values from project data
  const invoicedAmount = project.income || 0;
  const expenseAmount = project.expense || 0;
  const profitMargin = invoicedAmount - expenseAmount;
  let profitPercentage: any = "0";

  if (invoicedAmount !== 0) {
    profitPercentage = (profitMargin / invoicedAmount) * 100;
    profitPercentage = Math.max(-100, profitPercentage); // cap at -100%
    profitPercentage = profitPercentage.toFixed(1);
  } else {
    profitPercentage = "-100"; // or whatever you want for no income
  }

  return (
    <>
      {isDescriptionOpen && (
        <DescriptionModal
          isOpen={isDescriptionOpen}
          description={project.notes || ""}
          onClose={() => setIsDescriptionOpen(false)}
        />
      )}
      <div className="bg-white">
        {/* Header */}
        <div className="flex max-md:flex-col justify-between gap-4 lg:pb-6 sm:pb-4 bg-white sm:border-b border-gray-200">
          <div className="flex md:gap-4 gap-3">
            <button
              onClick={onBack}
              className="max-md:hidden self-start p-2 border hover:bg-gray-100 rounded-lg transition-colors"
            >
              <IoChevronBack className="w-5 h-5 text-black" />
            </button>
            <div className="flex flex-col">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <h1 className="xl:text-[20px] md:text-[18px] text-[17px] font-semibold text-black">
                    {project.name}
                  </h1>
                </div>
              </div>

              {/* Project Info */}
              <div className="flex items-center sm:gap-3 gap-2 max-sm:flex-wrap xl:text-[16px] md:text-[14px] text-[13px] text-gray-500 md:mt-4 mt-2 font-medium">
                <Link
                  to={`/customers/${project.customerId}/view`}
                  className="text-nowrap hover:text-blue-700"
                >
                  {project.customer || "Customer"}
                </Link>
                <span className="border-x-2 border-gray-200 sm:px-3 px-2 text-nowrap">
                  {formatDateRange(project.startDate, project.endDate)}
                </span>
                <span className="">
                  {project.notes ? (
                    <button
                      className="text-[#044E97] hover:text-[#044E97]"
                      onClick={() => setIsDescriptionOpen(true)}
                    >
                      Description
                    </button>
                  ) : (
                    "No description"
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="flex md:items-start justify-end gap-3">
            <OptionAction
              showFromTop={true}
              actionButton={
                <button className="xl:w-[160px] md:w-[135px] md:px-6 px-[14px] md:py-3 py-2 text-[#044E97] bg-[#E0EEFE] hover:text-white hover:bg-[#044E97] active:bg-[#226cb6] xl:text-[16px] md:text-[15px] text-[14px] text-nowrap sm:rounded-lg rounded-md transition-colors duration-200 font-medium">
                  Add Expense
                </button>
              }
              options={[
                {
                  value: "addNewExpense",
                  label: "Create New Expense",
                  onClick: onAddExpense,
                },
                {
                  value: "addExistingExpense",
                  label: "Add Existing Expense",
                  onClick: onAddExistingExpense,
                },
              ]}
            />
            <OptionAction
              showFromTop={true}
              actionButton={
                <button className="xl:w-[160px] md:w-[135px] md:px-6 px-[14px] md:py-3 py-2 text-[#044E97] bg-[#E0EEFE] hover:text-white hover:bg-[#044E97] active:bg-[#226cb6] xl:text-[16px] md:text-[15px] text-[14px] text-nowrap sm:rounded-lg rounded-md transition-colors duration-200 font-medium">
                  Add Invoice
                </button>
              }
              options={[
                {
                  value: "addNewInvoices",
                  label: "Create New Invoice",
                  onClick: canUseModule("invoices").allowed
                    ? onAddInvoice
                    : () => triggerSubscriptionModal("invoices"),
                },
                {
                  value: "addExistingInvoices",
                  label: "Add Existing Invoice",
                  onClick: onAddExistingInvoice,
                },
              ]}
            />
          </div>
        </div>

        {/* Content */}
        <div className="md:pt-8 pt-6">
          {/* Metric Cards */}
          <div className="flex max-md:flex-col md:flex-wrap xl:gap-5 md:gap-4 gap-3 mb-8">
            <MetricCard
              title="Invoiced"
              amount={invoicedAmount}
              bgColor="bg-[#ECFDF3]"
              textColor="text-black"
              isLoading={isLoading}
            />
            <MetricCard
              title="Project Expense"
              amount={expenseAmount}
              bgColor="bg-[#FFF8EC]"
              textColor="text-black"
              isLoading={isLoading}
            />
            <MetricCard
              title="Profit margin"
              amount={profitMargin}
              percentage={profitPercentage}
              bgColor="bg-[#F2F4F7]"
              textColor="text-black"
              isLoading={isLoading}
            />
          </div>

          {/* Bottom Section */}
          <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 xl:gap-5 md:gap-4 gap-3">
            {/* Profitability Chart */}
            <ProfitabilityChart
              invoiceAmount={invoicedAmount}
              expenseAmount={expenseAmount}
              isLoading={isLoading}
            />

            {/* Action Cards */}
            {/* <div className="xl:space-y-5 md:space-y-4 space-y-3">
              <ActionCard
                icon={
                  <ExpenseIconOrange className="xl:w-[58px] md:w-[52px] w-[46px] p-2 bg-[#FFF0D3] rounded-lg" />
                }
                title="Record New Expenses"
                description="Track what you have spent on this project."
                bgColor="bg-[#FFF8EC] hover:bg-[#FDF5E8]"
                onClick={onAddExpense}
              />
              <ActionCard
                icon={
                  <InvoiceIconOrange className="xl:w-[58px] md:w-[52px] w-[46px] p-2 bg-[#D1FADF] rounded-lg" />
                }
                title="Generate New Invoices"
                description="Request money for your products or services."
                bgColor="bg-[#ECFDF3] hover:bg-[#EBFAF1]"
                onClick={onAddInvoice}
              />
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewContainer;
